import React, { useMemo } from 'react';
import { endOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { IItemCreationSubsectionInputProps } from '~/Components';
import { Clock } from '~/Components/Icons';
import { ItemCreationValidInput } from '~/Components/ItemCreation/ItemCreationSubsection/ItemCreationSubsectionInput.model';
import { useDateFns, useHasAccess } from '~/Hooks';
import { ExtendedEventSeries } from '../../../extendedEventSeries.model';
import { AccessList } from '~/Hooks/useHasAccess/accessList.types';
import { EventScheduleTimeSeriesMonthlyInput } from '../EventScheduleTimeSeriesMonthlyInput';
import { TIME_FREQUENCY } from '~/Models/DTO/EventSeries.model';

export const useMonthlyRecurrence = ({
  timeSeries,
  hasFutureBookings,
}: {
  timeSeries: ExtendedEventSeries;
  hasFutureBookings?: boolean;
}) => {
  const { t } = useTranslation();
  const { formatDate, addTime } = useDateFns();
  const { hasAccess } = useHasAccess();
  const editingDisabled = hasFutureBookings && !hasAccess(AccessList.EDIT_SCHEDULE_WITH_BOOKINGS);

  return useMemo(() => {
    if (
      timeSeries.recurrence !== TIME_FREQUENCY.MONTHLY ||
      !hasAccess(AccessList.SCHEDULE_RECURRENCE_MONTHLY)
    ) {
      return [];
    }

    const today = formatDate(new Date().toISOString(), 'yyyy-MM-dd');
    const startDate = formatDate(timeSeries.startDate, 'yyyy-MM-dd');
    const minEnd =
      startDate < today ? new Date(`${today}T00:00:00`) : endOfDay(new Date(timeSeries.startDate));

    const children: IItemCreationSubsectionInputProps<Partial<ExtendedEventSeries>>[] = [
      {
        customElement: <EventScheduleTimeSeriesMonthlyInput startDate={timeSeries.startDate} />,
      },
      {
        label: t('event.creation.timeSeries.hasEnd'),
        type: ItemCreationValidInput.SWITCHER,
        name: 'hasEndDate',
        disabled: editingDisabled,
        icon: <Clock />,
      },
      {
        label: t('time.recurrence.repeatEvery'),
        type: ItemCreationValidInput.COUNTER,
        name: 'interval',
        disabled: editingDisabled,
        key: 'time.period.month',
        InputProps: {
          inputProps: {
            min: 1,
            formatValue: (count: number) => t('time.period.month', { count }),
          },
        },
      },
    ];
    if (timeSeries.hasEndDate) {
      children.push({
        label: t('time.endDate'),
        disabled: editingDisabled,
        placeholder: t('time.endDate'),
        type: ItemCreationValidInput.DATE,
        name: 'endDate',
        InputProps: { inputProps: { min: minEnd } },
        hasAnError: timeSeries.endDate < addTime(timeSeries.startDate, { days: -1 }).toISOString(),
      });
    }

    return children;
  }, [
    editingDisabled,
    timeSeries.endDate,
    timeSeries.interval,
    timeSeries.startDate,
    timeSeries.hasEndDate,
    timeSeries.frequency,
    timeSeries.recurrence,
    hasAccess,
  ]);
};
